import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    overheadRateOutsideFaculty: { type: Number, default: 11 },
    overheadRateOnFaculty: { type: Number, default: 22 }
  }

  connect() {
    this.recalculate()
  }

  recalculate() {
    const price = this.numberFromInput("preferred_price") ?? this.numberFromInput("price_per_student")
    const expectedStudentCount = this.numberFromInput("expected_student_count")
    const directWageCosts = this.numberFromInput("direct_wage_costs")
    const dppCosts = this.numberFromInput("dpp_costs")
    const directMaterialCosts = this.numberFromInput("direct_material_costs")
    const otherServicesPurchasesTotal = this.numberFromInput("other_services_purchases_total")
    const facultyOverhead = this.facultyOverhead()

    const administrativeCosts = this.multiply(expectedStudentCount, 135)
    const totalCourseRevenue = this.multiply(expectedStudentCount, price)
    const overheadCosts11 = this.multiply(totalCourseRevenue, this.overheadRateOutsideFacultyValue / 100)
    const overheadCosts22 = this.multiply(totalCourseRevenue, this.overheadRateOnFacultyValue / 100)
    const selectedOverheadCosts = facultyOverhead ? overheadCosts22 : overheadCosts11
    const totalCostsWithoutOverhead = this.sum(
      directWageCosts,
      dppCosts,
      directMaterialCosts,
      otherServicesPurchasesTotal,
      administrativeCosts
    )
    const indirectCostsOnFaculty = this.sum(administrativeCosts, overheadCosts22)
    const indirectCostsOutsideFaculty = this.sum(administrativeCosts, overheadCosts11)
    const totalRealizationCosts = this.sum(totalCostsWithoutOverhead, selectedOverheadCosts)
    const minimumCountCosts = ["interest_course", "professional_training", "applicant_course", "internal_course"].includes(this.element.dataset.courseType)
      ? totalRealizationCosts
      : this.sum(administrativeCosts, selectedOverheadCosts)
    const minimumStudentCount = this.ceiling(this.divide(minimumCountCosts, price))

    this.writeOutput("administrative_costs", administrativeCosts)
    this.writeOutput("minimum_student_count", minimumStudentCount)
    this.writeOutput("total_costs_without_overhead", totalCostsWithoutOverhead)
    this.writeOutput("total_realization_costs", totalRealizationCosts)
    this.writeOutput("indirect_costs_on_faculty", indirectCostsOnFaculty)
    this.writeOutput("indirect_costs_outside_faculty", indirectCostsOutsideFaculty)
    this.writeOutput("total_course_revenue", totalCourseRevenue)
    this.writeOutput("overhead_costs_11", overheadCosts11)
    this.writeOutput("overhead_costs_22", overheadCosts22)
    this.writeOutput("expected_student_count", expectedStudentCount)
    this.syncOverheadVisibility(facultyOverhead)
    this.syncRevenueWarning(totalCourseRevenue, totalRealizationCosts)
  }

  numberFromInput(key) {
    const element = this.element.querySelector(`[data-calculation-input="${key}"]`)
    if (!element) return null

    const normalizedValue = element.value.trim().replace(",", ".")
    if (normalizedValue.length === 0) return null

    const value = Number(normalizedValue)
    return Number.isFinite(value) ? value : null
  }

  heldAtValue() {
    const element = this.element.querySelector(`[data-calculation-input="held_at"]`)
    return element?.value || ""
  }

  facultyOverhead() {
    return ["on_faculty", "vv_stredisko_straz", "vv_stredisko_mulda", "Na fakultě", "VV středisko Stráž", "VV středisko Mulda"].includes(this.heldAtValue())
  }

  writeOutput(key, value) {
    const elements = this.element.querySelectorAll(`[data-calculation-output="${key}"]`)
    if (elements.length === 0) return

    const rawValue = value === null ? "" : this.formatNumber(value)
    elements.forEach((element) => {
      if ("value" in element) {
        element.value = rawValue
      } else {
        element.textContent = this.formatDisplayValue(value, element.dataset.calculationOutputFormat)
      }
    })
  }

  syncOverheadVisibility(onFaculty) {
    this.setOutputHidden("overhead_costs_11", onFaculty)
    this.setOutputHidden("overhead_costs_22", !onFaculty)
  }

  setOutputHidden(key, hidden) {
    const elements = this.element.querySelectorAll(`[data-calculation-output="${key}"]`)
    elements.forEach((element) => {
      const card = element.closest("[data-calculation-output-card]")
      if (card) card.hidden = hidden
    })
  }

  syncRevenueWarning(totalCourseRevenue, totalRealizationCosts) {
    const warning = totalCourseRevenue !== null && totalRealizationCosts !== null && totalCourseRevenue < totalRealizationCosts
    const elements = this.element.querySelectorAll(`[data-calculation-output="total_course_revenue"]`)
    elements.forEach((element) => {
      element.closest("[data-calculation-output-card]")?.classList.toggle("calculation-output-card--danger", warning)
    })
  }

  sum(...values) {
    if (values.every((value) => value === null)) return null

    return values.reduce((total, value) => total + (value ?? 0), 0)
  }

  multiply(...values) {
    if (values.some((value) => value === null)) return null

    return values.reduce((total, value) => total * value, 1)
  }

  divide(first, second) {
    if (first === null || second === null || second === 0) return null

    return first / second
  }

  ceiling(value) {
    if (value === null) return null

    return Math.ceil(value)
  }

  formatNumber(value) {
    return new Intl.NumberFormat("cs-CZ", {
      useGrouping: false,
      maximumFractionDigits: 0
    }).format(Math.round(value))
  }

  formatDisplayValue(value, format) {
    if (value === null) return "—"
    if (format === "currency") return `${this.formatGroupedNumber(value)} Kč`

    return this.formatGroupedNumber(value)
  }

  formatGroupedNumber(value) {
    return new Intl.NumberFormat("cs-CZ", {
      useGrouping: true,
      maximumFractionDigits: 0
    }).format(Math.round(value))
  }
}
